import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Analytics as AnalyticsIcon,
  Storage as StorageIcon,
  Timeline as TimelineIcon,
  Assessment as ReportIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { apiClient } from '../../services/api';

interface AnalyticsSettingsData {
  enabled: boolean;
  retentionDays: number;
  customReports: boolean;
}

interface AnalyticsSettingsProps {
  salonId: string;
}

const AnalyticsSettings: React.FC<AnalyticsSettingsProps> = ({ salonId }) => {
  const [settings, setSettings] = useState<AnalyticsSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { checkAccess, getRestrictions } = useSettings();
  
  const canWrite = checkAccess('analytics', 'write');
  const restrictions = getRestrictions('analytics');

  useEffect(() => {
    fetchSettings();
  }, [salonId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/settings/${salonId}/analytics`);
      
      if (response.success) {
        setSettings(response.data.settings);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load analytics settings');
    } finally {
      setLoading(false);
    }
  };

  const formatRetentionPeriod = (days: number) => {
    if (days < 30) return `${days} days`;
    if (days < 365) return `${Math.floor(days / 30)} months`;
    return `${Math.floor(days / 365)} years`;
  };

  const getStorageEstimate = (days: number) => {
    // Rough estimate: 1MB per day for a typical salon
    const mbPerDay = 1;
    const totalMB = days * mbPerDay;
    
    if (totalMB < 1024) return `~${totalMB}MB`;
    return `~${(totalMB / 1024).toFixed(1)}GB`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Alert severity="error">
        Failed to load analytics settings. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Analytics & Reporting</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {restrictions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {restrictions.map(restriction => (
                <Chip
                  key={restriction}
                  icon={<LockIcon />}
                  label={restriction.replace(/_/g, ' ')}
                  color="warning"
                  size="small"
                />
              ))}
            </Box>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSettings}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canWrite && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Read-Only Access
          </Typography>
          <Typography variant="body2">
            You can view analytics settings but cannot modify them. Contact your administrator to request changes.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Analytics Status */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AnalyticsIcon color="primary" />
                <Typography variant="h6">
                  Analytics Status
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color={settings.enabled ? 'success.main' : 'error.main'}>
                      {settings.enabled ? '✓' : '✗'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Analytics
                    </Typography>
                    <Chip 
                      label={settings.enabled ? 'Enabled' : 'Disabled'}
                      color={settings.enabled ? 'success' : 'error'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="info.main">
                      {formatRetentionPeriod(settings.retentionDays)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Data Retention
                    </Typography>
                    <Chip 
                      label={getStorageEstimate(settings.retentionDays)}
                      color="info"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color={settings.customReports ? 'primary.main' : 'text.disabled'}>
                      <ReportIcon />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Custom Reports
                    </Typography>
                    <Chip 
                      label={settings.customReports ? 'Available' : 'Standard Only'}
                      color={settings.customReports ? 'primary' : 'default'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="success.main">
                      <TrendingIcon />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Real-time
                    </Typography>
                    <Chip 
                      label="Active"
                      color="success"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Data Retention Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <StorageIcon color="primary" />
                <Typography variant="h6">
                  Data Retention
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemText
                    primary="Analytics Data Retention"
                    secondary={`Currently: ${formatRetentionPeriod(settings.retentionDays)}`}
                  />
                  <ListItemSecondaryAction>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={settings.retentionDays}
                        disabled={!canWrite}
                      >
                        <MenuItem value={30}>30 days</MenuItem>
                        <MenuItem value={90}>3 months</MenuItem>
                        <MenuItem value={180}>6 months</MenuItem>
                        <MenuItem value={365}>1 year</MenuItem>
                        <MenuItem value={730}>2 years</MenuItem>
                        <MenuItem value={1095}>3 years</MenuItem>
                      </Select>
                    </FormControl>
                  </ListItemSecondaryAction>
                </ListItem>
                
                <Divider />
                
                <ListItem>
                  <ListItemText
                    primary="Estimated Storage"
                    secondary={`Approximately ${getStorageEstimate(settings.retentionDays)} of data`}
                  />
                </ListItem>
              </List>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Longer retention periods provide better historical insights but require more storage space.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Report Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ReportIcon color="primary" />
                <Typography variant="h6">
                  Reporting Features
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemText
                    primary="Standard Reports"
                    secondary="Daily, weekly, and monthly business reports"
                  />
                  <ListItemSecondaryAction>
                    <Chip label="Included" color="success" size="small" />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <Divider />
                
                <ListItem>
                  <ListItemText
                    primary="Custom Reports"
                    secondary="Create custom analytics dashboards and reports"
                  />
                  <ListItemSecondaryAction>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.customReports}
                          disabled={!canWrite}
                        />
                      }
                      label=""
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <Divider />
                
                <ListItem>
                  <ListItemText
                    primary="Data Export"
                    secondary="Export analytics data to CSV/Excel"
                  />
                  <ListItemSecondaryAction>
                    <Chip label="Available" color="primary" size="small" />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Available Analytics */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Analytics
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Customer Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Customer acquisition trends
                      • Retention rates
                      • Lifetime value
                      • Demographics
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Revenue Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Daily/monthly revenue
                      • Service performance
                      • Payment method analysis
                      • Profit margins
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Operational Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Appointment patterns
                      • Staff utilization
                      • Service popularity
                      • Booking sources
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Marketing Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Campaign performance
                      • Customer acquisition cost
                      • ROI tracking
                      • Channel effectiveness
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Staff Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Performance metrics
                      • Revenue per staff
                      • Customer ratings
                      • Productivity trends
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Inventory Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Stock levels
                      • Product performance
                      • Reorder predictions
                      • Cost analysis
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Privacy & Compliance */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Privacy & Compliance
              </Typography>
              
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Data Protection Compliance
                </Typography>
                <Typography variant="body2">
                  All analytics data is processed in compliance with data protection regulations. 
                  Personal customer information is anonymized in analytics reports.
                </Typography>
              </Alert>
              
              <List>
                <ListItem>
                  <ListItemText
                    primary="Data Anonymization"
                    secondary="Customer personal data is anonymized in analytics"
                  />
                  <ListItemSecondaryAction>
                    <Chip label="Active" color="success" size="small" />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemText
                    primary="Secure Storage"
                    secondary="Analytics data is encrypted and securely stored"
                  />
                  <ListItemSecondaryAction>
                    <Chip label="Encrypted" color="success" size="small" />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemText
                    primary="Access Control"
                    secondary="Analytics access is controlled by user roles"
                  />
                  <ListItemSecondaryAction>
                    <Chip label="Role-Based" color="primary" size="small" />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsSettings;